import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterBloubergstrand() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-bloubergstrand",
    name: "Painters Bloubergstrand",
    location: "Bloubergstrand, Cape Town",
    rating: 4.8,
    reviews: 142,
    specialties: ["Coastal Properties", "Holiday Homes", "Interior Painting", "Exterior Painting"],
    image: "https://public.readdy.ai/ai/img_res/039c5b5dbfc7af82139e67b1a2bb0d67.jpg",
    verified: true,
    yearsExperience: 12,
    website: "https://paintersbloubergstrand.co.za",
    email: "info@paintersbloubergstrand.co.za",
    description: "Professional painting services in Bloubergstrand and surrounding coastal areas. We specialize in holiday homes and beachfront properties, with expertise in marine-grade finishes that withstand salt air and coastal weather conditions.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Holiday Home Painting",
      "Beachfront Property Painting",
      "Roof Painting & Coatings",
      "Waterproofing Services",
      "Damp Proofing Solutions",
      "Color Consultation & Design"
    ],
    serviceAreas: ["Bloubergstrand", "Table View", "Milnerton", "West Beach", "Big Bay", "Melkbosstrand", "Atlantis", "Coastal Areas"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20interior%20living%20room%20painted%20by%20professional%20painters%20in%20Bloubergstrand%2C%20modern%20coastal%20holiday%20home%20with%20fresh%20white%20walls%2C%20quality%20paint%20finish%2C%20Table%20Mountain%20view%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=bloubergstrand-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Bloubergstrand%20Cape%20Town%2C%20beachfront%20holiday%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20coastal%20setting%20with%20ocean%20view%2C%20well-maintained%20property&width=400&height=300&seq=bloubergstrand-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Holiday%20home%20painting%20project%20in%20Bloubergstrand%2C%20professional%20painters%20working%20on%20vacation%20property%20exterior%2C%20quality%20residential%20paint%20finish%2C%20modern%20coastal%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=bloubergstrand-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Kitchen%20interior%20painting%20in%20Bloubergstrand%20coastal%20home%2C%20fresh%20painted%20walls%2C%20modern%20kitchen%20design%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=bloubergstrand-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Sarah Mitchell",
        rating: 5,
        comment: "Excellent work on our beachfront holiday home. The painters understood the coastal challenges and delivered a beautiful, durable finish that has held up perfectly against the salt air."
      },
      {
        name: "David Thompson", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our Bloubergstrand home painting on schedule and the quality exceeded our expectations."
      },
      {
        name: "Lisa van der Merwe",
        rating: 4,
        comment: "Great attention to detail and very clean workers. Our holiday home looks amazing and we've received many compliments from guests."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Bloubergstrand | Professional Painting Services Cape Town | Coastal Property Specialists";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Bloubergstrand offers professional painting services for coastal properties in Bloubergstrand, Cape Town. Specializing in holiday homes, beachfront properties, and marine-grade finishes. Get a free quote today.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Bloubergstrand, house painters Bloubergstrand, painting contractors Cape Town, coastal property painting, beachfront painting services');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20coastal%20home%20in%20Bloubergstrand%20Cape%20Town%2C%20stunning%20Table%20Mountain%20views%20across%20ocean%2C%20painters%20in%20clean%20uniforms%20with%20marine-grade%20equipment%2C%20modern%20coastal%20architecture%2C%20bright%20natural%20lighting%20with%20iconic%20mountain%20backdrop%2C%20high%20quality%20coastal%20painting%20project&width=1200&height=600&seq=bloubergstrand-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Professional Painting Services in Bloubergstrand</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description}
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Coastal Painting Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Located along Cape Town's stunning West Coast, Bloubergstrand presents unique challenges for painting projects. The constant exposure to salt air, strong coastal winds, and intense UV radiation requires specialized knowledge and premium materials to ensure lasting results. Our team has over 12 years of experience working specifically in coastal environments, making us the ideal choice for your Bloubergstrand painting project.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Holiday Home and Beachfront Property Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Many properties in Bloubergstrand serve as holiday homes or rental properties, requiring painting solutions that not only look beautiful but can withstand periods of vacancy and varying levels of maintenance. We understand these unique requirements and use marine-grade paints and protective coatings that maintain their appearance and integrity even with minimal ongoing maintenance.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Materials for Coastal Conditions</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We exclusively use premium paints specifically formulated for coastal environments. These specialized products offer superior resistance to salt corrosion, UV fading, and moisture penetration. Our paint selection includes leading brands known for their durability in marine environments, ensuring your investment is protected for years to come.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Comprehensive Surface Preparation</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Proper surface preparation is crucial for coastal painting projects. Our thorough preparation process includes cleaning, sanding, priming, and treating any areas affected by salt damage or moisture. We pay special attention to areas most vulnerable to coastal conditions, such as window frames, door surrounds, and any metal fixtures that may be prone to corrosion.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20beachfront%20home%20in%20Bloubergstrand%20Cape%20Town%2C%20coastal%20holiday%20property%20with%20Table%20Mountain%20view%2C%20painters%20in%20clean%20uniforms%20with%20quality%20equipment%2C%20modern%20coastal%20architecture%2C%20bright%20natural%20lighting&width=400&height=300&seq=bloubergstrand-sidebar-image&orientation=landscape"
                      alt="Professional painters Bloubergstrand beachfront house painting services Cape Town Table Mountain"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Painting Services in Bloubergstrand</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Residential Painting Services</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our residential painting services are specifically designed for the unique challenges of coastal living in Bloubergstrand. From single rooms to complete home transformations, we provide comprehensive solutions that protect and beautify your property while withstanding the harsh coastal environment.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with marine-grade materials and expert coastal application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Interior Painting Excellence</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our interior painting services transform your Bloubergstrand home into a beautiful, comfortable living space. We use low-VOC paints that are safe for your family while providing excellent coverage and durability. Our color consultation services help you choose the perfect palette that complements your coastal lifestyle and maximizes natural light.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We understand that many Bloubergstrand properties are holiday homes or rental properties, so we work efficiently to minimize disruption while delivering exceptional results. Our team is experienced in working around furniture and can complete projects in stages if needed to accommodate your schedule.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Exterior Protection and Beauty</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Exterior painting in Bloubergstrand requires specialized knowledge of coastal conditions. We use marine-grade paints and protective coatings that resist salt corrosion, UV damage, and moisture penetration. Our comprehensive exterior painting process includes thorough surface preparation, primer application, and multiple coats of premium paint for maximum protection.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We also offer specialized services such as roof painting and waterproofing to provide complete protection for your coastal property. Our roof coatings not only enhance appearance but also provide additional insulation and weather protection, helping to reduce energy costs and extend roof life.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Painting Projects in Bloubergstrand</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Bloubergstrand and surrounding coastal areas. Each project demonstrates our commitment to quality workmanship and our expertise in coastal painting applications.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Bloubergstrand Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional coastal painting with marine-grade materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Bloubergstrand have to say about our painting services. Our commitment to excellence and customer satisfaction is reflected in these genuine reviews from homeowners and property managers in the area.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Bloubergstrand</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to transform your Bloubergstrand property with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, long-lasting results that enhance your coastal lifestyle.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding coastal areas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Quote Today</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation quote for your Bloubergstrand painting project. Our experienced team will assess your property and provide detailed recommendations tailored to coastal conditions.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
